/**
 * Fundamental Analysis panel (v1.4.0, Part 5): renders the Fundamental
 * Intelligence Engine's output — the overall fundamental score and the five
 * category scores (Valuation, Growth, Profitability, Financial Health,
 * Quality), each ratio's explained read, the cross-category tensions, and the
 * risk flags. Loads live from the company's latest fundamental snapshot.
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { FundamentalAnalysis, FundamentalCategoryScore } from "@/types";

function scoreClass(score: number | null): string {
  if (score === null) return "text-mist-400";
  if (score >= 66) return "text-signal-teal";
  if (score < 40) return "text-signal-rose";
  return "text-mist-200";
}

function readingSignalClass(signal: string): string {
  if (signal === "strong") return "text-signal-teal";
  if (signal === "weak") return "text-signal-rose";
  return "text-mist-400";
}

function CategoryCard({ category }: { category: FundamentalCategoryScore }): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-800/50 p-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-medium text-mist-100">{category.label}</span>
        <span className={`font-mono text-lg font-semibold ${scoreClass(category.score)}`}>
          {category.score === null ? "—" : category.score.toFixed(0)}
        </span>
      </button>
      <p className="mt-1 text-xs text-mist-400">{category.rating}</p>
      {open && category.readings.length > 0 && (
        <ul className="mt-2 space-y-1.5 border-t border-ink-700 pt-2">
          {category.readings.map((reading) => (
            <li key={reading.key} className="flex items-baseline gap-2 text-xs">
              <span className={`w-12 shrink-0 uppercase ${readingSignalClass(reading.signal)}`}>
                {reading.signal}
              </span>
              <span className="text-mist-300">{reading.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FundamentalAnalysisPanel({ companyId }: { companyId: string }): React.ReactElement | null {
  const [data, setData] = useState<FundamentalAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    apiClient
      .get<FundamentalAnalysis>(`/companies/${companyId}/fundamental-analysis`)
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load fundamental analysis.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (error) return null;

  if (isLoading || !data) {
    return (
      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Fundamental analysis</h2>
        <p className="mt-3 text-sm text-mist-400">Loading fundamental analysis…</p>
      </div>
    );
  }

  if (data.metrics_available === 0) {
    return (
      <div className="panel p-6">
        <h2 className="text-lg font-semibold text-mist-100">Fundamental analysis</h2>
        <p className="mt-3 text-sm text-mist-400">
          No fundamental snapshot on record yet. Once financials are ingested, this panel scores
          valuation, growth, profitability, financial health, and quality with an explanation for
          each ratio.
        </p>
      </div>
    );
  }

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-mist-100">Fundamental analysis</h2>
          <p className="mt-1 text-xs text-mist-400">
            {data.rating} · {data.metrics_available} ratios · confidence {(data.confidence * 100).toFixed(0)}%
          </p>
        </div>
        <p className="font-mono text-3xl font-semibold text-mist-100">
          {Number(data.overall_score).toFixed(0)}
          <span className="text-base text-mist-400">/100</span>
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.categories.map((category) => (
          <CategoryCard key={category.key} category={category} />
        ))}
      </div>
      <p className="mt-2 text-xs text-mist-400">Tap a category to see each ratio explained.</p>

      {data.tensions.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-signal-amber">Tensions</h3>
          <ul className="mt-1 space-y-1 text-sm text-mist-300">
            {data.tensions.map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>
      )}

      {data.risks.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-signal-rose">Risk flags</h3>
          <ul className="mt-1 space-y-1 text-sm text-mist-300">
            {data.risks.map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 border-t border-ink-800 pt-3 text-sm text-mist-300">{data.suggested_action}</p>
    </div>
  );
}
